/** Canonical product / content verticals. Overlap matching is exact-string. */
export const CAMPAIGN_CATEGORIES = Object.freeze([
  'Beauty',
  'Skincare',
  'Fashion',
  'Streetwear',
  'Sneakers',
  'Jewelry',
  'Food & beverage',
  'Home',
  'Electronics',
  'Toys',
  'Gaming',
  'Collectibles',
  'Sports & outdoors',
  'Baby & kids',
  'Pet supplies',
  'Automotive',
  'Health & wellness',
  'Entertainment',
  'Travel',
  'Lifestyle',
]);

/** Same 20 strings as campaigns so marketplace overlap matches. */
export const INFLUENCER_SIGNUP_CATEGORIES = CAMPAIGN_CATEGORIES;

export const CATEGORY_UNKNOWN_FALLBACK = 'Lifestyle';

export const CATEGORY_MAX_SELECT = 5;

/** Rail / header shorteners. Stored filter values stay the canonical labels. */
export const CATEGORY_RAIL_DISPLAY_LABELS = Object.freeze({
  'Food & beverage': 'Food & drink',
  'Sports & outdoors': 'Sports',
  'Health & wellness': 'Wellness',
  'Baby & kids': 'Baby',
  'Pet supplies': 'Pets',
  Electronics: 'Tech',
  Entertainment: 'Fun',
  Automotive: 'Auto',
});

/**
 * Old stored / URL labels → canonical. Keys are matched case-insensitively.
 * Keep in sync with millet/server/src/constants/categoryLabels.js
 */
export const CATEGORY_ALIASES_BY_CANONICAL = Object.freeze({
  Beauty: ['Hairstyling', 'Hair Care', 'Nail Art', 'Makeup', 'Hair'],
  Fashion: ["Women's clothing", "Men's clothing", 'Luxury Fashion', 'Luxury', "Women's fashion", "Men's"],
  'Food & beverage': [
    'Food',
    'Cooking',
    'Baking',
    'Beverages',
    'Coffee',
    'Cocktails',
    'Healthy Eating',
    'Restaurant Reviews',
    'Nutrition',
    'Food & drink',
    'Food & Beverage',
  ],
  Home: ['Home Decor', 'DIY & Crafts', 'Organization'],
  Electronics: ['Tech Reviews', 'Tech'],
  Gaming: ['Video Games'],
  'Sports & outdoors': ['Outdoors', 'Sports', 'Hiking', 'Camping', 'Running', 'Dance', 'Adventure'],
  'Baby & kids': ['Babycare', 'Parenting', 'Baby Products', 'Kids Activities', 'Baby'],
  'Pet supplies': ['Pets'],
  Automotive: ['Auto'],
  'Health & wellness': [
    'Fitness',
    'Workout',
    'Yoga',
    'Wellness',
    'Self Care',
    'Mental Health',
    'Spirituality',
    'Weight Loss',
    'Health & Fitness',
  ],
  Entertainment: ['Music', 'Movies', 'Books', 'Anime', 'Pop Culture', 'Streaming', 'Fun', 'Movies & TV'],
  Travel: ['Nature', 'City Guides', 'Budget Travel'],
  Lifestyle: [
    'Photography',
    'Photos',
    'Education',
    'Student Life',
    'Career Tips',
    'Personal Finance',
    'Productivity',
  ],
});

const CANONICAL_BY_LOWER = Object.freeze(
  Object.fromEntries(CAMPAIGN_CATEGORIES.map((label) => [label.toLowerCase(), label])),
);

const ALIAS_TO_CANONICAL = (() => {
  const map = { ...CANONICAL_BY_LOWER };
  Object.entries(CATEGORY_ALIASES_BY_CANONICAL).forEach(([canonical, aliases]) => {
    aliases.forEach((alias) => {
      map[String(alias).toLowerCase()] = canonical;
    });
  });
  return Object.freeze(map);
})();

const ALIASES_BY_CANONICAL_LOWER = (() => {
  const map = {};
  CAMPAIGN_CATEGORIES.forEach((canonical) => {
    map[canonical] = [canonical, ...(CATEGORY_ALIASES_BY_CANONICAL[canonical] || [])];
  });
  return Object.freeze(map);
})();

export function canonicalizeCategoryLabel(label, fallback = CATEGORY_UNKNOWN_FALLBACK) {
  if (label == null) return fallback;
  const trimmed = String(label).trim();
  if (!trimmed) return fallback;
  const mapped = ALIAS_TO_CANONICAL[trimmed.toLowerCase()];
  if (mapped) return mapped;
  if (fallback === null) return trimmed;
  return fallback;
}

export function canonicalizeCategoryList(labels, {
  fallback = CATEGORY_UNKNOWN_FALLBACK,
  max = CATEGORY_MAX_SELECT,
} = {}) {
  if (!Array.isArray(labels) || labels.length === 0) return [];
  const seen = new Set();
  const next = [];
  labels.forEach((label) => {
    const canonical = canonicalizeCategoryLabel(label, fallback);
    if (!canonical || seen.has(canonical)) return;
    seen.add(canonical);
    next.push(canonical);
  });
  if (max == null) return next;
  return next.slice(0, max);
}

/** URL / facet values: map aliases, keep unknown labels (do not force Lifestyle). */
export function canonicalizeFilterCategories(labels) {
  return canonicalizeCategoryList(labels, { fallback: null, max: null });
}

/** Expand a filter so overlap matches both old stored strings and the new 20. */
export function expandCategoryFilterLabels(labels) {
  const expanded = new Set();
  canonicalizeFilterCategories(labels).forEach((label) => {
    const canonical = canonicalizeCategoryLabel(label, label);
    (ALIASES_BY_CANONICAL_LOWER[canonical] || [canonical, label]).forEach((alias) => {
      if (alias) expanded.add(alias);
    });
  });
  return [...expanded];
}

export function getCategoryRailDisplayLabel(name) {
  if (name == null) return '';
  const trimmed = String(name).trim();
  if (!trimmed) return '';
  const canonical = canonicalizeCategoryLabel(trimmed, trimmed);
  return CATEGORY_RAIL_DISPLAY_LABELS[canonical] || CATEGORY_RAIL_DISPLAY_LABELS[trimmed] || trimmed;
}
